import { AumChart } from "components/stats/AUM";
import Layout from "../components/Layout";
import { Card, CircularProgress, Grid, Tab, Tabs } from "@mui/material";
import { PlChart } from "components/stats/PL";
import { StrategyGroupsList } from "components/clients/StrategyGroupsList";
import { SyntheticEvent, useMemo, useState } from "react";
import { useAuth } from "features/store";
import { useListStrategyGroupsQuery } from "features/trading/accounts";

// The charts tab is not a strategy group, so it is keyed by a value no group id
// can collide with.
const AUM_PL_TAB = "aum_pl";

export default function HomePage() {
  const { isAuthenticated } = useAuth();
  // Shares RTK Query's cache with StrategyGroupsList, which reads the same
  // groups to build its tables -- one request feeds both, so the argument has
  // to stay identical to the one there or the cache entry splits in two. The
  // page size is raised past the endpoint's default 10 because a group beyond
  // that page would have no tab, and the tab is now the only route to its
  // table.
  const { data: groupsData, isLoading: isGroupsLoading } =
    useListStrategyGroupsQuery(
      { filters: { page_size: 100 } },
      { skip: !isAuthenticated },
    );
  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  // One tab per strategy group, in the order the API returns them, then the
  // charts. A group added or renamed server-side gets its tab with no code
  // change here.
  const tabs = useMemo(() => {
    const groupTabs = (groupsData?.results || []).map((group) => ({
      value: String(group.id),
      label: group.name,
    }));

    return [...groupTabs, { value: AUM_PL_TAB, label: "AUM & P&L" }];
  }, [groupsData]);

  // The first tab until the user picks one -- and again if the tab they had
  // picked is no longer in the list.
  const activeTab =
    tabs.find((tab) => tab.value === selectedTab)?.value || tabs[0].value;

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  // The tab strip is built from the groups, so it waits for them rather than
  // rendering a strip that grows a tab at a time as the request lands. The
  // authentication check is part of that wait: until it resolves the query is
  // skipped, and a skipped query reports isLoading false -- which would render
  // a strip holding nothing but the charts tab, and select it.
  if (!isAuthenticated || isGroupsLoading) {
    return (
      <Layout title="Home" content="Home page">
        <CircularProgress />
      </Layout>
    );
  }

  return (
    <Layout title="Home" content="Home page">
      <Card>
        {/* Scrollable because the group list is server-side and unbounded: the
            standard variant clips the tabs it cannot fit, with no way to reach
            them. "auto" still renders the arrows disabled once everything fits,
            so hide those -- an arrow that does nothing reads as broken. */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Strategy groups"
          sx={{ "& .MuiTabs-scrollButtons.Mui-disabled": { display: "none" } }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              id={`home-tab-${tab.value}`}
              value={tab.value}
              label={tab.label}
            />
          ))}
        </Tabs>
        <Grid
          container
          spacing={2}
          padding={2}
          role="tabpanel"
          aria-labelledby={`home-tab-${activeTab}`}
        >
          {/* Stays mounted on the charts tab -- it owns the strategy query and
              the Factset streaming subscriptions, which a tab switch must not
              tear down and re-establish. */}
          <Grid item xs={12}>
            <StrategyGroupsList
              activeGroupId={activeTab === AUM_PL_TAB ? null : Number(activeTab)}
            />
          </Grid>
          {activeTab === AUM_PL_TAB && (
            <>
              {/* Stacked, not side by side: a full-width chart gives each bar
                  enough room that a year of daily bars stays readable. */}
              <Grid item xs={12}>
                <AumChart />
              </Grid>
              <Grid item xs={12}>
                <PlChart />
              </Grid>
            </>
          )}
        </Grid>
      </Card>
    </Layout>
  );
}
